import { Pivot, PivotItem, Spinner } from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { EntityAPI, EntityType, IContentHubType, IEntityToken, IProvisionProfile, ISensitivityLabel, ISiteTemplate, IUserConfigPackage, ProvisionType, SecurityMode, SharingCapabilities } from '../../../API';
import ProvisionProfileEditor, { ProvisionProfileEditorState } from './ProvisionProfileEditor';
import { EntityTypeEditorState } from '../entity-types/EntityTypeEditor';

export class ProvisionProfilesState {
	
	public userConfig: IUserConfigPackage;

	public loading: boolean = false;
	public loaded: boolean = false; 

	public profiles: ProvisionProfileEditorState[] = [];

	public templateFilesUrl: string | undefined;
	public shouldDisplayAddTemplateMessage = false;
	public shouldDisplayResyncMessage = false;
	
	public ids: string[];
	public tokens?: IEntityToken[];
	pluralName: string;
	listUrl: string;
	entityType: EntityTypeEditorState;
	
	
	constructor(userConfig: IUserConfigPackage, entityType:EntityTypeEditorState, ids:string[], listUrl:string) {
		this.userConfig = userConfig;
		this.ids = ids;
		this.pluralName = entityType.PluralName;
		this.entityType = entityType;
		this.listUrl = listUrl;

		makeAutoObservable(this);
	}
	
	public setListUrl(arg0: string) {
		this.listUrl = arg0;
		this.profiles.forEach(p => p.setListUrl(arg0));
	}
	
	public async saveAll() : Promise<void> {
		var api = await this.getApi();
		
		for (const profile of this.profiles) {
			profile.Id = await api.upsert(profile.thisProfile(), []);
		}
	}

	public static getProvisionTypeName(type:ProvisionType) : string {
		if(type === ProvisionType.NoWorkspace) {
			return 'No Workspace';
		} else if(type === ProvisionType.TeamChannel) {
			return 'Team Channel';
		} else if(type === ProvisionType.Team) {
			return 'Microsoft Team';
		} else if (type === ProvisionType.Site) {
			return 'SharePoint Site';
		} else if (type === ProvisionType.Library) {
			return 'Document Library';
		} else if (type === ProvisionType.Folder) {
			return 'Folder'
		} else {
			return '';
		}

	}
	
	public selectedProfileIndex: string = '0';
	public setSelectedProfileIndex(arg0: string) {
		this.selectedProfileIndex = arg0;
	}

	public setAvailableFields(listUrlColumns: import("../../../API").IEntityColumn[]) {
		this.profiles.forEach(p => p.setAvailableFields(listUrlColumns));
	}
	
	public setAvailableSiteTemplates(siteTemplates: ISiteTemplate[]) {
		this.profiles.forEach(p => p.setAvailableSiteTemplates(siteTemplates));
	}

	public setAvailableContentTypes(contentTypes: IContentHubType[]) {
		this.profiles.forEach(p => p.setAvailableContentTypes(contentTypes));
	}
	
	public setAvailableContentHubColumns(columns: string[]) {
		this.profiles.forEach(p => p.metadataFields.setAvailableContentHubColumns(columns));
	}

	public setSensitivityLabels(arg0: ISensitivityLabel[]) {
		this.profiles.forEach(p => p.setSensitivityLabels(arg0));
	}
	
	public setTokens(tokens:IEntityToken[]) {
		this.tokens = tokens;
		this.profiles.forEach(p => p.setTokens(tokens));
	}

	public async load(): Promise<void> {
		this.loading = true;
		if(this.ids.length > 0) {
			let api = await this.getApi();
			let profiles = await api.items(this.ids, null);
			this.setItems(profiles);
		} else {
			this.setItems([]);
		}
	}

	private async getApi() : Promise<EntityAPI<IProvisionProfile>> {
		return await EntityAPI.fromEntityType(
			EntityType.ProvisionProfile,
			this.userConfig,
		) as EntityAPI<IProvisionProfile>;
	}

	ensureInitialLoaded() {
		if (!this.loading && !this.loaded) {
			this.load();
		}
	}

	setItems(items: IProvisionProfile[]) {
		this.loading = false;
		this.loaded = true;
		this.profiles = items.map(item => new ProvisionProfileEditorState(this.userConfig, this.pluralName, this.entityType, item, this.listUrl));

		this.profiles.forEach(p => {
			p.setShowProfileInfo(this.profiles.length > 1);
		});
	}

	public removeProfile() {
		let selectedProfile = this.profiles[Number(this.selectedProfileIndex) || 0];
		if(selectedProfile) {
			this.profiles = this.profiles.filter(p => p !== selectedProfile);
			this.setSelectedProfileIndex('0');
		}
	}

	public addNewProfile(profileInfo?:IProvisionProfile) {

		var newProfile:IProvisionProfile = profileInfo || {
			Id: '',
			Name: 'New workspace profile',
			SiteTemplate: 'STS#3',
			SiteSharingCapabilities: SharingCapabilities.Disabled,
			ProvisionType: ProvisionType.Site,
			WorkspaceTitle: '{{Item.Title}}',
			WorkspacePath: '',
			WorkspacePathComponent: '{{Item.ID}}',
			OneDriveSyncPath: 'Workspaces/{{Item.Title}}',
			SecurityMode: SecurityMode.None,
			PrivacyExpression: '',
			MetadataPrivacyExpression: '',
			Expression: '',
			CreateWorkspaceExpression: '',
			TemplateTriggerIds: [],
			TriggerIds: [],
			AIPropertyIds: [],
			SecurityMemberFields: [],
			SecurityOwnerFields: [],
			SecurityVisitorFields: [],
			SecurityMembers: [],
			SecurityOwners: [],
			SecurityVisitors: [],
			EnableOneDriveSync: false,
			MetadataFields: [],
			FavouritesPeopleFields: [],
			FavouritesPeople: [],
			ContentTypeIds: [],
			MoveAttachmentsToWorkspace: true,
			MoveAttachmentsToWorkspacePath: '',
			SensitivityLabelExpression: '',
			MailNickname: '',
			Description: '',
			MetadataSecurityMode: SecurityMode.None,
			MetadataSecurityMemberFields: [],
			MetadataSecurityOwnerFields: [],
			MetadataSecurityVisitorFields: [],
			MetadataSecurityMembers: [],
			MetadataSecurityOwners: [],
			MetadataSecurityVisitors: [],

			//Boring required fields that we should get rid of
			DisplayTitle: '',
			ServerRelativeUrl: '',
			NativeMetadataUrl: '',
		};

		let state = new ProvisionProfileEditorState(this.userConfig, this.pluralName, this.entityType, newProfile, this.listUrl);
		this.profiles = this.profiles.concat([state]);

		this.profiles.forEach(p => {
			p.setShowProfileInfo(this.profiles.length > 1);
		});
	}
}

function ProvisionProfiles({ state }: { state: ProvisionProfilesState }) {
	useEffect(() => {
		state.ensureInitialLoaded();
	}, [state]);


	if (state.loading) {
		return <Spinner></Spinner>;
	}

	return (<>
			
			{state.profiles.length > 0 && <>

				{ state.profiles.length === 1 ? 
					<ProvisionProfileEditor state={state.profiles[0]} /> 
					:
					<Pivot
						selectedKey={state.selectedProfileIndex}
						onLinkClick={(p) => { state.setSelectedProfileIndex(p?.props.itemKey || ''); }}>

						{state.profiles.map((profile, idx) => (
							<PivotItem headerText={profile.Name} itemKey={idx.toString()} key={profile.Id || 'new'}>
								<ProvisionProfileEditor state={profile} />
							</PivotItem>
						))}
					</Pivot>
				}
			</>}
		</>
	);
}

export default observer(ProvisionProfiles);
